import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useContentLocalizer } from '../../hooks/useContentLocalizer';
import { useCommon } from '../../contexts/CommonContext';
import { useTenant } from '../../contexts/TenantContext';
import { BdsButton, BdsIcon, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import Breadcrumb from '../Breadcrumb';
import { BreadcrumbItem } from '../../hooks/useBreadcrumb';
import {
  SubheaderContainer,
  SubheaderContent,
  SubheadContentGeneral,
  SubheaderLeft,
  SubheaderRight,
  SubheaderContainerLink,
} from './styles';
import { localization } from './localization';
import { createTrack } from '../../api/SegmentApi';
import {
  action,
  AGENT_CONSUMPTION_CLICK,
  ORGANIZATION_AGENTS,
  origin,
  REPORT_AGENT_CONSUMPTION_OPEN,
} from '../../shared/SegmentEvents';
import { AGENT_CONSUMPTION, DATA_CONSUMPTION } from '../../shared/FeatureToggleKeys';
import { accessPermission, hasPermissions } from '../../services/PermissionService';
import { BillingMetrics } from '../../shared/BillingMetrics';
import FeatureToggleService from '../../services/FeatureToggleService';

const Subheader: React.FC = () => {
  const content = useContentLocalizer(localization);
  const location = useLocation();
  const {
    common: { headerContent, loggedUser },
  } = useCommon();
  const {
    tenant: { info: tenant, subscription },
  } = useTenant();

  const agentConsumptionRoute =
    location.pathname === '/old-agent-consumption' || location.pathname === '/agent-consumption';
  const agentConsumptionMonthlyRoute =
    location.pathname === '/old-agent-consumption/monthly' || location.pathname === '/agent-consumption/monthly';
  const agentRoute = location.pathname === '/agent';
  const hasMaxWidth =
    location.pathname === '/old-agent-consumption' || location.pathname === '/old-agent-consumption/monthly';
  const [agentConsumptionVisibility, setAgentConsumptionVisibility] = useState(false);
  const [oldAgentConsumptionVisibility, setOldAgentConsumptionVisibility] = useState(false);

  useEffect(() => {
    const getAgentConsumptionVisibility = async () => {
      const isAgentConsumptionPageVisible = await hasPermissions(
        subscription,
        loggedUser,
        accessPermission.billing,
        [BillingMetrics.agent],
        [{key: AGENT_CONSUMPTION}],
      );
      const isDataConsumptionEnabled = await FeatureToggleService.isFeatureToggleEnabled(DATA_CONSUMPTION);
      setAgentConsumptionVisibility(isAgentConsumptionPageVisible && agentRoute && isDataConsumptionEnabled);
      setOldAgentConsumptionVisibility(isAgentConsumptionPageVisible && agentRoute && !isDataConsumptionEnabled);
    };
    getAgentConsumptionVisibility();
  }, [loggedUser, location.pathname, subscription, agentRoute]);

  const trackPage = (trackingKey, origin: origin) => {
    createTrack(tenant, trackingKey, {
      user: loggedUser.email,
      action: action.CLICK,
      origin: origin,
    });
  };

  // Check if headerContent has breadcrumb items, otherwise use the legacy format or default breadcrumb
  const defaultBreadcrumbItems: BreadcrumbItem[] = [{ label: content.contractPanel || 'Home' }];

  const breadcrumbItems = headerContent.breadcrumbItems || defaultBreadcrumbItems;
  const isLastItemEditable = headerContent.lastItemEditable || false;
  const onEditLastItem = headerContent.onEditLastItem;

  // Check if we should use the legacy format (when no breadcrumbItems are provided but text is)
  const useLegacyFormat = !headerContent.breadcrumbItems && headerContent.text;

  return (
    <SubheaderContainer className="bg-color-surface-1">
      <SubheaderContent hasMaxWidth={hasMaxWidth}>
        <SubheadContentGeneral>
          <SubheaderLeft>
            {useLegacyFormat ? (
              headerContent.redirect && (
                <>
                  <SubheaderContainerLink to={headerContent.redirect}>
                    <BdsIcon name="arrow-ball-left" size="large"></BdsIcon>
                  </SubheaderContainerLink>
                  <BdsTypo tag="span" bold="bold" variant="fs-20">
                    {headerContent.text ?? content.contractPanel}
                  </BdsTypo>
                </>
              )
            ) : (
              // New format: use breadcrumb component
              <Breadcrumb
                items={breadcrumbItems}
                lastItemEditable={isLastItemEditable}
                onEditLastItem={onEditLastItem}
              />
            )}
          </SubheaderLeft>
          <SubheaderRight>
            {(agentConsumptionRoute || agentConsumptionMonthlyRoute) && (
              <Link to={{ pathname: '/agent' }}>
                <BdsButton
                  onClick={() => trackPage(ORGANIZATION_AGENTS, origin.AGENTCONSUMPTION)}
                  variant="tertiary"
                  size="standard"
                >
                  {content.agents}
                </BdsButton>
              </Link>
            )}
            {oldAgentConsumptionVisibility && (
              <Link to={{ pathname: '/old-agent-consumption' }}>
                <BdsButton
                  onClick={() => trackPage(AGENT_CONSUMPTION_CLICK, origin.AGENT)}
                  variant="tertiary"
                  size="standard"
                >
                  {content.agentConsumption}
                </BdsButton>
              </Link>
            )}
            {agentConsumptionVisibility && (
              <Link to={{ pathname: '/agent-consumption' }}>
                <BdsButton
                  onClick={() => trackPage(REPORT_AGENT_CONSUMPTION_OPEN, origin.AGENT)}
                  variant="tertiary"
                  size="standard"
                >
                  {content.agentConsumption}
                </BdsButton>
              </Link>
            )}
          </SubheaderRight>
        </SubheadContentGeneral>
      </SubheaderContent>
    </SubheaderContainer>
  );
};

export default Subheader;
